import { Link } from 'react-router-dom'

export default function RateLimitsGuide() {
  return (
    <div>
      <h1>Rate limits</h1>
      <p>
        <em>Limits exist because cloud bills do.</em>
      </p>
      <p>
        Every request to the API is metered against one or more tiered
        token-bucket budgets. Most endpoints sit on the generous default tier;
        a few that trigger real-world cost (web searches, email enrichment,
        AI scoring) are throttled more aggressively.
      </p>

      <h2>How it works</h2>
      <ul>
        <li>
          Every request decrements one token from each bucket that applies to
          its tier.
        </li>
        <li>
          Buckets refill continuously — the per-minute bucket refills at
          one-sixtieth of its capacity per second.
        </li>
        <li>
          When any bucket reaches zero, the API returns{' '}
          <code>429 Too Many Requests</code> with a{' '}
          <code>Retry-After</code> header (seconds).
        </li>
        <li>
          Limits are <strong>per API key</strong>. Cost-tier limits
          (<code>expensive</code>, <code>medium_cost</code>) are{' '}
          <em>also</em> enforced per-org, so multiple keys in one org share
          one cost budget.
        </li>
      </ul>

      <h2>Tiers</h2>
      <table style={tableStyle}>
        <thead>
          <tr>
            <th style={thStyle}>Tier</th>
            <th style={thStyle}>per&nbsp;min</th>
            <th style={thStyle}>per&nbsp;hour</th>
            <th style={thStyle}>per&nbsp;day</th>
            <th style={thStyle}>Applies to</th>
          </tr>
        </thead>
        <tbody>
          <tr>
            <td style={tdStyle}><code>default</code></td>
            <td style={tdStyle}>600</td>
            <td style={tdStyle}>10,000</td>
            <td style={tdStyle}>50,000</td>
            <td style={tdStyle}>Every public endpoint (a safety net for accidental loops).</td>
          </tr>
          <tr>
            <td style={tdStyle}><code>write</code></td>
            <td style={tdStyle}>120</td>
            <td style={tdStyle}>2,000</td>
            <td style={tdStyle}>20,000</td>
            <td style={tdStyle}>POST/PATCH/PUT/DELETE on non-cost-sensitive routes.</td>
          </tr>
          <tr>
            <td style={tdStyle}><code>cheap_llm</code></td>
            <td style={tdStyle}>30</td>
            <td style={tdStyle}>500</td>
            <td style={tdStyle}>5,000</td>
            <td style={tdStyle}>
              <code>POST /sourcing/prospects/smart_search</code>,{' '}
              <code>draft_outreach</code>,{' '}
              <code>POST /candidates/{'{'}id{'}'}/reparse</code>.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}><code>medium_cost</code></td>
            <td style={tdStyle}>10</td>
            <td style={tdStyle}>100</td>
            <td style={tdStyle}>500</td>
            <td style={tdStyle}>
              <code>POST /sourcing/prospects/{'{'}id{'}'}/enrich_email</code>.
              Each call costs us money at FullEnrich. Also enforced per-org.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}><code>expensive</code></td>
            <td style={tdStyle}>3</td>
            <td style={tdStyle}>30</td>
            <td style={tdStyle}>100</td>
            <td style={tdStyle}>
              <code>POST /sourcing/missions</code>,{' '}
              <code>/missions/{'{'}id{'}'}/refine</code>,{' '}
              <code>POST /deep_searches</code>. Each triggers a real web
              search and/or batched LLM scoring. Also enforced per-org.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}><code>outreach_send</code></td>
            <td style={tdStyle}>5</td>
            <td style={tdStyle}>60</td>
            <td style={tdStyle}>200</td>
            <td style={tdStyle}>
              <code>POST /sourcing/prospects/{'{'}id{'}'}/send_outreach</code>{' '}
              and <code>queue_outreach</code>.
            </td>
          </tr>
          <tr>
            <td style={tdStyle}><code>outreach_per_domain</code></td>
            <td style={tdStyle}>—</td>
            <td style={tdStyle}>5</td>
            <td style={tdStyle}>50</td>
            <td style={tdStyle}>
              Per recipient-domain throttle on outreach sends. Protects your
              org's sender reputation — Gmail does not forgive bulk
              cold-emails to <code>gmail.com</code>.
            </td>
          </tr>
        </tbody>
      </table>

      <p>
        When a route applies multiple tiers (e.g. <code>outreach_send</code>{' '}
        AND <code>outreach_per_domain</code>), <strong>the most restrictive
        bucket wins</strong>.
      </p>

      <h2>Response headers</h2>
      <p>
        Successful responses carry headers describing the current state of the
        most-restrictive tier on the route:
      </p>
      <pre>
        <code>{`HTTP/1.1 200 OK
X-RateLimit-Limit-minute: 30
X-RateLimit-Remaining-minute: 27
X-RateLimit-Reset-minute: 60
X-RateLimit-Limit-hour: 500
X-RateLimit-Remaining-hour: 487
X-RateLimit-Reset-hour: 3600
...`}</code>
      </pre>

      <h2>When you're rate-limited</h2>
      <pre>
        <code>{`HTTP/1.1 429 Too Many Requests
Retry-After: 17
Content-Type: application/json

{
  "detail": {
    "detail": "Rate limit exceeded: 3 per minute on tier 'expensive'",
    "tier": "expensive",
    "retry_after": 17
  }
}`}</code>
      </pre>
      <p>
        Wait at least <code>Retry-After</code> seconds before trying again.
        Exponential backoff on top of that is a friendly thing to do but not
        required.
      </p>

      <h2>Per-org cost budgets</h2>
      <p>
        For the <code>expensive</code> and <code>medium_cost</code> tiers,
        limits are enforced <em>per-org</em> in addition to per-key. This
        prevents a single org from running 50 keys in parallel to drain a
        single shared cost budget. A second key in the same org consuming the
        same tier is charging the same org bucket.
      </p>

      <h2>Why the per-domain throttle on outreach</h2>
      <p>
        Sender reputation is a real cost — separate from per-call API spend
        and harder to recover. If a single org cold-emails 50 addresses on{' '}
        <code>gmail.com</code> in an hour, Gmail's filters notice and the
        org's deliverability suffers across <em>all</em> their candidate
        outreach for days afterward. The per-domain bucket caps that at 5
        sends per hour, 50 per day to any single recipient domain.
      </p>

      <h2>Need higher limits?</h2>
      <p>
        If your integration legitimately needs a higher tier (e.g. you're a
        platform reselling our API, or you've got a one-time batch import
        and don't want to babysit a backoff loop), email{' '}
        <a href="mailto:support@chosenhq.com">support@chosenhq.com</a> with
        the API key prefix (<code>cho_xxxxxxxx</code>) and the rough volume
        you need. We'll do real math, not the polite kind.
      </p>

      <h2>For curious people</h2>
      <p>
        The implementation is a per-process in-memory token bucket. Numbers
        in the table are the live values in{' '}
        <code>backend/app/core/rate_limiting.py</code>; if those drift from
        what this page says, the code wins. We currently run a single
        backend replica per environment — when we scale that out, each
        replica will enforce its own bucket, so effective limits will become{' '}
        N × the stated number. We'll update this page when that happens.
      </p>
      <p>
        Looking for the rest of the API contract?{' '}
        <Link to="/guides/errors">Errors</Link> and{' '}
        <Link to="/guides/versioning">Versioning</Link>.
      </p>
    </div>
  )
}

const tableStyle: React.CSSProperties = {
  borderCollapse: 'collapse',
  width: '100%',
  margin: '1rem 0 1.5rem',
  fontSize: '0.9rem',
}
const thStyle: React.CSSProperties = {
  textAlign: 'left',
  padding: '0.5rem 0.75rem',
  borderBottom: '2px solid var(--border-strong)',
  background: 'var(--code-bg)',
  fontWeight: 600,
}
const tdStyle: React.CSSProperties = {
  padding: '0.5rem 0.75rem',
  borderBottom: '1px solid var(--border)',
  verticalAlign: 'top',
}
